import styled from '@emotion/styled'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { useRepository } from '~/@common/hooks/useRepository'
import { WORKSHEET_WRITE_KEY } from '~/@common/models/(Permission)/permissionUtils'
import { errorHandlerService } from '~/@common/services'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { colors, typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { Icon } from '~/@common/ui/Icon/Icon'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import GlobalHeader from '~/@pages/@widgets/(Navigation)/GlobalHeader/GlobalHeader'
import { StudentLearningContentBackConfirmModal } from '~/@pages/student/@widgets/learningContent/StudentLearningContentBackConfirmModal'

import { useSubmitWorksheetScoring } from '../@common/hooks/useSubmitWorksheetScoring'
import { QrWorksheetScoringService } from '../@service/QrWorksheetScoring.service'

export const WorksheetScoringPageHeader = ({ title }: { title: string }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const service = useRepository(QrWorksheetScoringService)
  const handle = useSubmitWorksheetScoring()

  const navigate = useNavigate()

  const scoringSize = searchParams.get('size')

  // SlotRight 모드 전환 + 답안 제출 모달 둘 다 학습지 write 권한 있을 때만 의미가 있음
  const isWorksheetWriteEnabled = commonRepo.permission.byFeature.isAllowed(WORKSHEET_WRITE_KEY)

  const defaultPrevRouteName = routeName.student.studentWorksheet

  const navigateBack = () => {
    // QR 진입이지만 본인에게 출제되지 않은(또는 validation 응답이 비어있는) 학습지면
    // studentWorksheetId 가 null — 그대로 path 에 박으면 /student/student-worksheet/null/scoring
    // 으로 빠지므로 학습지 목록(defaultPrevRouteName) 으로 fallback.
    const studentWorksheetId = service.validation?.studentWorksheetId
    if (studentWorksheetId == null) {
      navigate(defaultPrevRouteName, { replace: true })
      return
    }

    const studentWorksheetUrl =
      scoringSize === 'one'
        ? `/student/student-worksheet/${studentWorksheetId}`
        : `/student/student-worksheet/${studentWorksheetId}/scoring`

    navigate(studentWorksheetUrl, {
      replace: true,
    })
  }

  return (
    <PortalRootLayoutHeader>
      <GlobalHeader
        SlotCenter={title}
        SlotRight={
          isWorksheetWriteEnabled ? (
            scoringSize === 'all' ? (
              <RightLink onClick={() => setSearchParams('size=one')}>
                <Icon name="icon_description_paper" size={20} color={colors.gray.$900} />
                <span>한문제씩</span>
              </RightLink>
            ) : (
              <RightLink onClick={() => setSearchParams('size=all')}>
                <Icon name="icon_list" size={20} color={colors.gray.$900} />
                <span>빠른채점</span>
              </RightLink>
            )
          ) : null
        }
        prevLinkProps={{
          to: defaultPrevRouteName,
          onClick: (e) => {
            e.preventDefault()
            // write 권한 차단(학부모 등) → 답안 제출 모달 자체 미노출, 즉시 뒤로
            if (isWorksheetWriteEnabled && service.problemScoringColl?.toScoredArr.length) {
              modalService.openModal(
                <StudentLearningContentBackConfirmModal
                  defaultPrevRouteName={defaultPrevRouteName}
                  onSubmit={async () => {
                    try {
                      await handle?.onSubmit()
                      navigateBack()
                    } catch (error) {
                      errorHandlerService.handle(error)
                    }
                  }}
                />,
                {
                  modalName: '답안제출_안내',
                },
              )
              return
            }
            navigateBack()
          },
        }}
      />
    </PortalRootLayoutHeader>
  )
}

const RightLink = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  ${typo.caption01};
  color: ${colors.gray.$900};
  font-weight: bold;

  cursor: pointer;

  ${mediaQuery.underDesktop} {
    ${typo.body02};
    font-weight: normal;
    color: ${colors.gray.$600};
    svg {
      color: ${colors.gray.$600};
    }
  }
`
